/**
 * Scalar argument validators shared by every deal tool: ids, the search query,
 * and the result limit.
 *
 * Split out of `../deal-validation.ts`. The two id validators were byte-identical
 * apart from the noun in their messages, including a second copy of the UUID
 * pattern; they now share one `validateUuid` so a change to what counts as a
 * valid id cannot land on one entity and miss the other.
 *
 * Inputs are `unknown` rather than `any` — these are called with raw model
 * output, so narrowing is the whole job (main-03).
 */

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Default page size when the caller sends none. */
const DEFAULT_LIMIT = 20;

/** Longest search string we will forward to the CRM. */
const MAX_SEARCH_QUERY_CHARS = 500;

/**
 * Shared id check. `entity` is the human noun used in both messages ("Deal",
 * "Company") so the caller still learns WHICH id was wrong.
 */
function validateUuid(id: unknown, entity: string): string {
  if (!id || typeof id !== 'string') {
    throw new Error(`${entity} ID is required and must be a string`);
  }
  if (!UUID_PATTERN.test(id)) {
    throw new Error(`Invalid ${entity.toLowerCase()} ID format: ${id}`);
  }
  return id;
}

/** Validate deal ID */
export function validateDealId(id: unknown): string {
  return validateUuid(id, 'Deal');
}

/** Validate company ID */
export function validateCompanyId(id: unknown): string {
  return validateUuid(id, 'Company');
}

/** Validate search query */
export function validateSearchQuery(query: unknown): string {
  if (!query || typeof query !== 'string') {
    throw new Error('Search query is required and must be a string');
  }

  const trimmed = query.trim();
  if (trimmed.length === 0) {
    throw new Error('Search query cannot be empty');
  }

  if (trimmed.length > MAX_SEARCH_QUERY_CHARS) {
    throw new Error(`Search query cannot exceed ${MAX_SEARCH_QUERY_CHARS} characters`);
  }

  return trimmed;
}

/**
 * Validate limit parameter.
 *
 * Absent → the default. Over the cap → the cap (clamped, not rejected: the model
 * asking for 500 rows is a hint, not an error). Anything else must be a number
 * of at least 1, floored.
 */
export function validateLimit(limit: unknown, max: number = 100): number {
  if (limit === undefined || limit === null) {
    return DEFAULT_LIMIT;
  }

  if (typeof limit !== 'number' || limit < 1) {
    throw new Error('Limit must be a positive number');
  }

  if (limit > max) {
    return max;
  }

  return Math.floor(limit);
}
